import { State, StoreActionTypes } from '../types'
import type { GridKeys } from './actions'
import { State as SettingsState } from './reducer'

import { StickyGridState, WatchlistWindowSetting } from './types'
import { QuoteReliability } from '../securities/types'
import { defaultFilter } from '../filter/types'

export const actionTypesToTriggerSaving = [
  'securities.setWatchListId',
  'securities.setIssuerFilter',
  'securities.setSecuritiesFilter',
  'securities.setIsMine',
  'securities.setShowLive',
  'securities.setShowRfqEligible',
  'securities.saveAdvancedFilter',
  'securities.setUseAdvancedFilter',
  'securities.setQuoteReliability',
  'securities.removeGrid',
  'grid.updateColumnsOrder',
  'depthofmarket.popout',
  'depthofmarket.closePopout',
  'depthofmarket.closeAllPopouts',
  'depthofmarket.updatePopouts',
  'order.setMyOrdersOpen',
  'alerts.muteAlert',
  'alerts.unmuteAlert',
  'settings.updateGridState',
  'settings.saveListTradingTemplateSelection'
] satisfies StoreActionTypes[]

export const injectWindowSettingsIntoState = (
  settings: WatchlistWindowSetting[],
  StickyGridStates: Record<
    KeysWithValsOfType<SettingsState, StickyGridState>,
    StickyGridState | undefined
  >,
  mutedTradingNowAlerts: any[],
  mutedCounteredAlerts: any[],
  mutedToppedAlerts: any[],
  DepthSecurityIds: number[],
  maxDOMs: number,
  state: State
): State => {
  const tempState = JSON.parse(JSON.stringify(state))
  tempState.alerts.mutedAlerts.tradingNow = mutedTradingNowAlerts
  tempState.alerts.mutedAlerts.countered = mutedCounteredAlerts
  tempState.alerts.mutedAlerts.topped = mutedToppedAlerts
  settings.forEach((a, i) => {
    tempState.grid.columnsOrder[i] = a.columnDefinitions
    tempState.order.myOrdersIsOpen[i] = a.myOrdersIsOpen
    tempState.securities.gridDefinitions[i] = {
      watchlistId: a.watchlistId ?? undefined,
      isMine: a.isMine,
      myFirm: false,
      myBook: false,
      showLive: a.showLive,
      showRfqEligible: a.showRfqEligible,
      quoteReliability: a.qr,
      order: 0,
      filter: {
        ...a.filter,
        price: undefined,
        yield: undefined,
        bidAskDiff: undefined,
        bidAskDiffGreaterLessThan: defaultFilter.bidAskDiffGreaterLessThan,
        ageOfIssue: 0
      },
      size: a.size,
      isMineWatchlist: a.isMineWatchlist,
      useAdvancedFilter: a.useAdvancedFilter
    }
  })
  tempState.settings = {
    ...tempState.settings,
    areSettingsLoaded: true,
    ...StickyGridStates
  }
  tempState.depthofmarket.popoutSecurityIds = DepthSecurityIds ?? []
  tempState.depthofmarket.maxDOMs = maxDOMs
  return tempState
}

export const MAX_FILTER_VALUE_LENGTH = 100

interface FilterCondition {
  filter?: unknown
  filterTo?: unknown
  conditions?: FilterCondition[]
}

const isOversized = (value: unknown) =>
  typeof value === 'string' && value.length > MAX_FILTER_VALUE_LENGTH

const hasOversizedValue = (condition: FilterCondition | undefined): boolean =>
  condition?.conditions
    ? condition.conditions.some(hasOversizedValue)
    : isOversized(condition?.filter) || isOversized(condition?.filterTo)

/*
  All the admin grids share one 65535-char backend field, so a filter value long enough to
  threaten it is dropped rather than truncated — a shortened filter would come back matching
  more rows than the user set up.
*/
export const storableGridState = (
  gridState: StickyGridState | undefined
): StickyGridState | undefined => {
  const filterModel = gridState?.filterModel
  if (!filterModel) return gridState

  const storable = Object.fromEntries(
    Object.entries(filterModel).filter(
      ([, columnFilter]) => !hasOversizedValue(columnFilter as FilterCondition)
    )
  )

  return Object.keys(storable).length === Object.keys(filterModel).length
    ? gridState
    : { ...gridState, filterModel: storable }
}

/*
  `satisfies Record<GridKeys, string>` is what makes this exhaustive — adding a sticky-grid
  key to the settings slice fails to compile here until it is mapped, so it cannot be left
  out of the payload or the cap.
*/
export const gridStateDtoKeys = {
  aggressAttemptSettings: 'aggressAttemptWindowSettings',
  rfqSettings: 'rfqWindowSettings',
  passiveOrdersSettings: 'passiveOrdersWindowSettings',
  tradeBlotterSettings: 'tradeBlotterWindowSettings',
  guiUserDataSettings: 'guiUserDataWindowSettings',
  externalLiquiditySettings: 'externalLiquidityWindowSettings',
  apiUserDataSettings: 'apiUserDataWindowSettings',
  listTradingSettings: 'listTradingWindowSettings',
  userDashboardSettings: 'userDashboardWindowSettings'
} satisfies Record<GridKeys, string>

const storableGridStates = (settings: SettingsState) =>
  Object.fromEntries(
    Object.entries(gridStateDtoKeys).map(([stateKey, dtoKey]) => [
      dtoKey,
      storableGridState(settings[stateKey as GridKeys])
    ])
  )

export const settingsObject = (state: { value: State }) => {
  const arr = []
  for (const property in state.value.securities.gridDefinitions) {
    if (state.value.securities.gridDefinitions.hasOwnProperty(property)) {
      const val = state.value.securities.gridDefinitions[property]
      const watchlistWindowSetting: WatchlistWindowSetting = {
        watchlistId: val.watchlistId === undefined ? null : val.watchlistId,
        showLive: val.showLive,
        showRfqEligible: val.showRfqEligible,
        myFirm: val.myFirm,
        myBook: val.myBook,
        qr: val.quoteReliability,
        isMine: val.isMine,
        myOrdersIsOpen: state.value.order.myOrdersIsOpen[property],
        columnDefinitions: state.value.grid.columnsOrder[property],
        columnState: [],
        filterModel: null,
        filter: val.filter,
        size: val.size,
        isMineWatchlist: val.isMineWatchlist,
        useAdvancedFilter: val.useAdvancedFilter
      }

      arr.push(watchlistWindowSetting)
    }
  }

  const s = {
    watchlistWindowSettings: arr,
    ...storableGridStates(state.value.settings),
    mutedTradingNowAlerts: state.value.alerts.mutedAlerts.tradingNow,
    mutedCounteredAlerts: state.value.alerts.mutedAlerts.countered,
    mutedToppedAlerts: state.value.alerts.mutedAlerts.topped,
    DepthSecurityIds: state.value.depthofmarket.popoutSecurityIds,
    maxDOMs: state.value.depthofmarket.maxDOMs,
    listTradingTemplateSetting: state.value.settings.listTradingTemplateSetting
  }
  return s
}

export const defaultBackendSettings = {
  watchlistWindowSettings: [
    {
      showLive: false,
      showRfqEligible: false,
      myFirm: false,
      myBook: false,
      myOrdersIsOpen: false,
      isMine: false,
      qr: QuoteReliability.All,
      order: 0,
      filter: defaultFilter,
      size: 0,
      useAdvancedFilter: false,
      columnDefinitions: []
    }
  ],
  aggressAttemptWindowSettings: null,
  apiUserDataWindowSettings: null,
  externalLiquidityWindowSettings: null,
  guiUserDataWindowSettings: null,
  listTradingSettings: null,
  passiveOrdersWindowSettings: null,
  rfqWindowSettings: null,
  tradeBlotterWindowSettings: null,
  mutedTradingNowAlerts: [],
  mutedCounteredAlerts: [],
  mutedToppedAlerts: [],
  DepthSecurityIds: [],
  maxDOMs: 20,
  listTradingTemplateSetting: { template: 'Standard', isSpread: true }
}
